import { ILogger } from '@principle-theorem/developer-tools';
import {
  AppointmentStatus,
  AutomationStatus,
  IAutomation,
  IBrand,
  PatientCollection,
  TimingDirection,
  TreatmentStepAutomation,
  type IAppointment,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  addDoc,
  asyncForEach,
  collectionGroupQuery,
  getDoc,
  getDocs,
  isSameRef,
  patchDoc,
  resolveSequentially,
  snapshot,
  toISODate,
  toTimestamp,
  where,
  type DocumentReference,
  type RequireProps,
  type Timestamp,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import * as moment from 'moment-timezone';
import { Appointment } from '../models/appointment/appointment';
import { Automation } from '../models/automation/automation';
import { Brand } from '../models/brand';
import { TreatmentStep } from '../models/clinical-charting/treatment/treatment-step';
import { TimezoneResolver } from '../timezone';

export class AddAutomationsToFutureAppointments {
  constructor(private _logger: ILogger) {}

  async run(
    brandRef: DocumentReference<IBrand>,
    startDate: Timestamp,
    endDate?: Timestamp
  ): Promise<void> {
    const brand = await getDoc(brandRef);
    const practices = await snapshot(Brand.practices$(brand));
    await asyncForEach(practices, async (practice) => {
      await this._seedPracticeAutomations(
        brand,
        practice.ref,
        startDate,
        endDate
      );
    });
  }

  private async _seedPracticeAutomations(
    brand: WithRef<IBrand>,
    practiceRef: DocumentReference<IPractice>,
    startDate: Timestamp,
    endDate?: Timestamp
  ): Promise<void> {
    const appointments = await getDocs(
      collectionGroupQuery<RequireProps<IAppointment, 'event'>>(
        PatientCollection.Appointments,
        ...compact([
          where('practice.ref', '==', practiceRef),
          where('event.from', '>=', startDate),
          endDate ? where('event.from', '<=', endDate) : undefined,
        ])
      )
    );

    await resolveSequentially(appointments, async (appointment) => {
      if (appointment.deleted) {
        this._logger.info(`Appointment is deleted - skipping`);
        return;
      }

      this._logger.info(
        `Processing appointment with date ${toISODate(appointment.event.from)}`
      );
      if (appointment.status === AppointmentStatus.Cancelled) {
        this._logger.info(`Appointment is cancelled - skipping`);
        return;
      }

      const treatmentStep = await Appointment.treatmentStep(appointment);
      const timezone = await TimezoneResolver.fromEvent(appointment.event);
      const existingAutomations = await getDocs(
        TreatmentStep.automationCol(treatmentStep)
      );

      if (existingAutomations.length) {
        this._logger.info(
          `Appointment already has ${existingAutomations.length} automations`
        );

        await resolveSequentially(existingAutomations, async (automation) => {
          if (automation.status !== AutomationStatus.Pending) {
            return;
          }

          this._logger.info(`Fixing automation ${automation.ref.path}`);

          await patchDoc(automation.ref, {
            status: AutomationStatus.Cancelled,
          });

          await patchDoc(automation.ref, {
            status: AutomationStatus.Pending,
          });
        });
      }

      const triggerAfterDate = Appointment.getCompletedAt(appointment);
      const brandAutomationConfigurations = await snapshot(
        Automation.getAutomations$(brand, appointment.event, triggerAfterDate)
      );

      const treatmentAutomationConfigurations = await snapshot(
        TreatmentStep.getTreatmentConfigurationAutomations$(
          treatmentStep,
          appointment.event,
          brand.ref,
          triggerAfterDate
        )
      );

      const automations = [
        ...brandAutomationConfigurations,
        ...treatmentAutomationConfigurations,
      ].filter((automation) => {
        const isImmediately =
          automation.data.timing.direction === TimingDirection.Immediately;
        const hasExistingAutomation = this._hasExistingAutomation(
          automation,
          existingAutomations
        );
        return !isImmediately && !hasExistingAutomation;
      });

      if (!automations.length) {
        return;
      }

      this._logger.info(
        `Adding ${automations.length} automations to step: ${treatmentStep.ref.path}`
      );

      await asyncForEach(automations, async (automation) => {
        const now = moment.tz(timezone);

        const expectedTriggerDate = Automation.getExpectedTriggerDate(
          automation,
          appointment.event,
          timezone
        );

        const triggerDate = Automation.applyGracePeriod(
          expectedTriggerDate,
          timezone,
          now
        );

        if (expectedTriggerDate.isBefore(now, 'day')) {
          this._logger.info(`TriggerDate is before today. Skipping.`);
          return;
        }

        await addDoc(TreatmentStep.automationCol(treatmentStep), {
          ...automation,
          triggerDate: toTimestamp(triggerDate),
        });
      });
    });
  }

  private _hasExistingAutomation(
    automation: IAutomation<TreatmentStepAutomation>,
    existingAutomations: WithRef<IAutomation<TreatmentStepAutomation>>[]
  ): boolean {
    return existingAutomations.some((existingAutomation) =>
      isSameRef(existingAutomation.configRef, automation.configRef)
    );
  }
}
